import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Switch from 'LuxKit/Switch/Switch'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineTransactionIcon from 'LuxKit/Icons/line/LineTransactionIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import { LOYALTY_REWARDS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import useLoyaltySpendPointsRemoteConfig from 'loyalty/hooks/useLoyaltySpendPointsRemoteConfig'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import LoyaltySpendPointsTourCarousel from './LoyaltySpendPointsTourCarousel'
import LoyaltySpendPointsCruiseCarousel from './LoyaltySpendPointsCruiseCarousel'
import LoyaltySpendPointsExperienceCarousel from './LoyaltySpendPointsExperienceCarousel'
import LoyaltySpendPointsHero from './LoyaltySpendPointsHero'
import LoyaltySpendPointsHotelCarousel from './LoyaltySpendPointsHotelCarousel'
import LoyaltySpendPointsMaldivesCarousel from './LoyaltySpendPointsMaldivesCarousel'
import LoyaltySpendPointsShopCarousel from './LoyaltySpendPointsShopCarousel'
import { WITHIN_BALANCE_MINIMUM_POINTS } from './loyaltySpendPointsConstants'

import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'

export type { SpendPointsSortOption } from './loyaltySpendPointsTypes'

const Page = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const InfoCardLink = styled(ClickableLink)`
  color: inherit;
  text-decoration: underline;
`

const ControlsSection = styled(LayoutContainer)`
  margin-bottom: ${rem(16)};
`

const SortControl = styled.div`
  flex: none;
  position: relative;
`

const SortButton = styled(TextButton)`
  && {
    font-size: 14px;
    line-height: 20px;
  }
`

const SortOptions = styled(Group)`
  padding-inline: ${rem(16)};
`

const BalanceToggle = styled.label`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  padding: ${rem(11)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
`

const HowToUseSection = styled.section`
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding-block: ${rem(32)} ${rem(36)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(36)} ${rem(40)};
  }
`

const InfoCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  max-width: ${rem(440)};
  margin: ${rem(24)} auto 0;

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(2, ${rem(440)});
    max-width: ${rem(896)};
  }
`

const InfoCard = styled.div`
  display: grid;
  grid-template-columns: ${rem(48)} 1fr;
  gap: ${rem(16)};
  align-items: center;
  min-height: ${rem(116)};
  padding: ${rem(24)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const OffersSection = styled.section`
  padding-block: ${rem(24)} ${rem(8)};
`

const WaysToBoostSection = styled.section`
  padding-block: ${rem(40)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const BoostTiles = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const BoostTile = styled.article`
  display: block;
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const BoostTileImageContainer = styled.div`
  aspect-ratio: 16 / 7;
`

const BoostTileImage = styled(Image)`
  display: block;
  width: 100%;
  height: 100%;
`

const BoostTileContent = styled(Group)`
  padding: ${rem(24)};
`

const MobileCarouselTitle = styled.span`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const DesktopCarouselTitle = styled.span`
  display: none;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
    align-items: center;
    gap: ${rem(12)};
  }
`

function LoyaltySpendPointsPage() {
  const intl = useIntl()
  const userTier = useAppSelector(getLoyaltyDisplayTier)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isMerchandiseEnabled = useAppSelector(getIsMerchandiseEnabled)
  const [sortOption, setSortOption] = useState<SpendPointsSortOption>('recommended')
  const [showWithinBalanceOnly, setShowWithinBalanceOnly] = useState(false)
  const { value: isSortOpen, toggle: toggleSort, off: closeSort } = useToggle()
  const { waysToBoostTiles } = useLoyaltySpendPointsRemoteConfig()
  const sortTriggerRef = useRef<HTMLButtonElement>(null)
  const programName = getLoyaltyProgramDisplayName()
  const availablePoints = loyaltyAccount?.points ?? 0
  const isWrongRegion = isLoggedIn && hasLoyaltyAccount && !!loyaltyAccountRegion && !isInLoyaltyRegion
  const showWithinBalanceToggle = isLoggedIn && !isWrongRegion && availablePoints >= WITHIN_BALANCE_MINIMUM_POINTS
  const showOffersWithinBalance = showWithinBalanceToggle && showWithinBalanceOnly
  const burnPointsLimit = isLoggedIn && !isWrongRegion && availablePoints >= MINIMUM_POINTS_TO_APPLY
    ? availablePoints
    : undefined
  const pricingContextValue = useMemo(() => ({
    burnPointsLimit,
    showOffersWithinBalance,
    isSpendPointsPage: true,
  }), [burnPointsLimit, showOffersWithinBalance])
  const isAURegion = currentRegionCode === 'AU'
  const isAuOrNzRegion = currentRegionCode === 'AU' || currentRegionCode === 'NZ'
  const showShopCarousel = isAURegion && isMerchandiseEnabled
  const sortOptions = useMemo(() => [
    {
      value: 'recommended' as const,
      label: intl.formatMessage({
        id: 'ByMpLdKGI_wnk1d3WYWD',
        defaultMessage: 'Recommended',
        description: 'Recommended sort option for spend points page offers',
      }),
    },
    {
      value: 'lowest-points' as const,
      label: intl.formatMessage({
        id: '4kU7IEH3-XbyfuI02RGl',
        defaultMessage: 'Lowest Points',
        description: 'Lowest points sort option for spend points page offers',
      }),
    },
    {
      value: 'highest-points' as const,
      label: intl.formatMessage({
        id: 'KNvlmdk6AhJ-M04oiZyg',
        defaultMessage: 'Highest Points',
        description: 'Highest points sort option for spend points page offers',
      }),
    },
  ], [intl])
  const selectedSortOption = sortOptions.find(option => option.value === sortOption) ?? sortOptions[0]
  const onSortChange = useCallback((value: SpendPointsSortOption) => {
    setSortOption(value)
    closeSort()
  }, [closeSort])
  const onWithinBalanceChange = useCallback(() => {
    setShowWithinBalanceOnly(value => !value)
  }, [])
  const maldivesTitle = intl.formatMessage({
    id: 'zMAFOrL2ZmwJd9ANjUVG',
    defaultMessage: 'Maldives bundles with <i>{programName} Rewards</i>',
    description: 'Heading for Maldives reward bundle carousel on the Societe spend points page',
  }, {
    i: (chunks: ReactNode) => <i>{chunks}</i>,
    programName,
  })
  const maldivesMobileTitle = intl.formatMessage({
    id: 'yKJQizrErQOlzdu6ZfHU',
    defaultMessage: 'Maldives bundles with {programName} Rewards',
    description: 'Mobile heading for Maldives reward bundle carousel on the Societe spend points page',
  }, { programName })

  const tourTitle = intl.formatMessage({
    id: 'u6ESAR2juDwfuaLHfu2e',
    defaultMessage: '<i>Tour</i> offers',
    description: 'Heading for the tours carousel on the Societe spend points page',
  }, {
    i: (chunks: ReactNode) => <i>{chunks}</i>,
  })

  return <LoyaltySpendPointsPricingContext.Provider value={pricingContextValue}>
    <Page>
      <LoyaltySpendPointsHero
        isLoggedIn={isLoggedIn}
        isAURegion={isAURegion}
        points={availablePoints}
        programName={programName}
        userTier={userTier}
      />

      {isAURegion && <HowToUseSection>
        <LayoutContainer variant="large">
          <Heading variant="heading2" as="h2" align="center">
            {intl.formatMessage({
              id: 'nF3T6c52tZbLbWNvA0xl',
              defaultMessage: 'How to use {programName} Points',
              description: 'Section heading explaining how to use Societe points',
            }, { programName })}
          </Heading>

          <InfoCards>
            <InfoCard>
              <LineTransactionIcon size={40} />
              <Group direction="vertical" gap={8}>
                <Heading variant="heading6" as="h3">
                  {intl.formatMessage({
                    id: 'd37NORn2omOW_oN2cxLt',
                    defaultMessage: '{programName} Points + Pay',
                    description: 'Heading for Points + Pay card on the Societe spend points page',
                  }, { programName })}
                </Heading>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'n7rVGX7euZbDAxi52E6J',
                    defaultMessage: 'Use your points to pay for all or part of your next escape — simply choose how many to apply at checkout and save instantly.',
                    description: 'Body copy for Points + Pay card on the Societe spend points page',
                  })}
                </BodyText>
              </Group>
            </InfoCard>

            <InfoCard>
              <ClassicRewardsSealIcon size={40} colour="highlight-primary-dark" />
              <Group direction="vertical" gap={8}>
                <Heading variant="heading6" as="h3">
                  {intl.formatMessage({
                    id: 'w8xHFl6NDTN5aAqzDT-_',
                    defaultMessage: '{programName} Rewards',
                    description: 'Heading for Societe Rewards card on the Societe spend points page',
                  }, { programName })}
                </Heading>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'tuEgkSj6qFK2wG_blQkX',
                    defaultMessage: 'Get more for your points. {programName} Rewards stays offer a higher redemption rate than Points + Pay — so your balance goes further.',
                    description: 'Body copy for Societe Rewards card on the Societe spend points page',
                  }, { programName })}
                </BodyText>
                <BodyTextBlock variant="medium">
                  <InfoCardLink to={`/${LOYALTY_REWARDS_PAGE}`}>
                    {intl.formatMessage({
                      id: 'HB6UVyPH-Q9fqxhyWyQx',
                      defaultMessage: 'Learn more',
                      description: 'Learn more link text for Societe Rewards on the Societe spend points page',
                    })}
                  </InfoCardLink>
                </BodyTextBlock>
              </Group>
            </InfoCard>
          </InfoCards>
        </LayoutContainer>
      </HowToUseSection>}

      <OffersSection>
        <ControlsSection>
          <HorizontalContainerWithFadedOverflow gap={16} overflowGutter={16} verticalAlign="center">
            {showWithinBalanceToggle && (
              <BalanceToggle>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'eD36-ME5h2oQ0UYrSjws',
                    defaultMessage: 'Show offers within my balance',
                    description: 'Toggle label for filtering spend points page offers within the user points balance',
                  })}
                </BodyText>
                <Switch checked={showWithinBalanceOnly} onChange={onWithinBalanceChange} />
              </BalanceToggle>
            )}

            <SortControl>
              <SortButton
                ref={sortTriggerRef}
                kind="secondary"
                variant="default"
                size="large"
                onClick={toggleSort}
                endIcon={isSortOpen ? <LineAngleUpIcon /> : <DropdownChevron />}
                aria-label={intl.formatMessage({
                  id: '1zU2cmYZeK4S6so03ptA',
                  defaultMessage: 'Sort offers',
                  description: 'Accessible label for spend points page offer sort control',
                })}
              >
                {intl.formatMessage({
                  id: 'KQItyfXIPAEQgjABstqE',
                  defaultMessage: 'Sort: {option}',
                  description: 'Desktop sort button showing current sort option on the form',
                }, { option: selectedSortOption.label })}
              </SortButton>
              <DropdownList size="S" open={isSortOpen} onClose={closeSort} anchorRef={sortTriggerRef} triggerRef={sortTriggerRef} placement="bottom-start" portaled>
                <SortOptions direction="vertical" gap={16}>
                  {sortOptions.map(option => <RadioInput
                    key={option.value}
                    checked={sortOption === option.value}
                    name="spend-points-sort"
                    value={option.value}
                    onChange={() => onSortChange(option.value)}
                  >
                    {option.label}
                  </RadioInput>)}
                </SortOptions>
              </DropdownList>
            </SortControl>
          </HorizontalContainerWithFadedOverflow>
        </ControlsSection>

        {isAURegion && <LoyaltySpendPointsMaldivesCarousel
          sortOption={sortOption}
          title={
            <>
              <MobileCarouselTitle>{maldivesMobileTitle}</MobileCarouselTitle>
              <DesktopCarouselTitle>
                <ClassicRewardsSealIcon size={32} colour="highlight-primary-dark" aria-hidden />
                {maldivesTitle}
              </DesktopCarouselTitle>
            </>
          }
        />}
        <LoyaltySpendPointsHotelCarousel
          sortOption={sortOption}
          title={intl.formatMessage({
            id: 'N_js-cZxhQAGmcZfTfU1',
            defaultMessage: '<i>Hotel</i> offers',
            description: 'Heading for hotel offers carousel on the Societe spend points page',
          }, {
            i: (chunks: ReactNode) => <i>{chunks}</i>,
          })}
        />
        <LoyaltySpendPointsExperienceCarousel
          sortOption={sortOption}
          title={intl.formatMessage({
            id: 'I1gCwWxkrUnMKSRdObS0',
            defaultMessage: '<i>Experience</i> offers',
            description: 'Heading for experience offers carousel on the spend points page',
          }, {
            i: (chunks: ReactNode) => <i>{chunks}</i>,
          })}
        />
        {showShopCarousel && <LoyaltySpendPointsShopCarousel sortOption={sortOption} />}
        {isAuOrNzRegion && <LoyaltySpendPointsTourCarousel title={tourTitle} sortOption={sortOption} />}
        {isAuOrNzRegion && <LoyaltySpendPointsCruiseCarousel
          sortOption={sortOption}
          title={intl.formatMessage({
            id: 'qYL4FGqU2SSHuvDgW8hv',
            defaultMessage: '<i>Cruise</i> offers',
            description: 'Heading for cruise offers carousel on the spend points page',
          }, {
            i: (chunks: ReactNode) => <i>{chunks}</i>,
          })}
        />}
        {!isAuOrNzRegion && <LoyaltySpendPointsTourCarousel
          includeCruises
          sortOption={sortOption}
          title={intl.formatMessage({
            id: 'ShumvxeT2nZQbIifUcYy',
            defaultMessage: '<i>Tours & Cruises</i>',
            description: 'Heading for combined tours and cruises carousel on the spend points page',
          }, {
            i: (chunks: ReactNode) => <i>{chunks}</i>,
          })}
        />}
      </OffersSection>

      {waysToBoostTiles.length > 0 && <WaysToBoostSection>
        <LayoutContainer variant="large">
          <Heading variant="heading2" as="h2" align="center">
            {intl.formatMessage({
              id: 'VfBZI5indzkSE3Fx_b4R',
              defaultMessage: 'Ways to boost your {programName} Points balance',
              description: 'Heading for ways to boost points section on the spend points page',
            }, { programName })}
          </Heading>
          <BoostTiles>
            {waysToBoostTiles.map(tile => (
              <BoostTile key={tile.ctaUrl}>
                <BoostTileImageContainer>
                  <BoostTileImage id={tile.imageId} fit="center" objectFit="cover" />
                </BoostTileImageContainer>
                <BoostTileContent direction="vertical" gap={4}>
                  <Heading variant="heading5" as="h3">{tile.title}</Heading>
                  <BodyText variant="medium">{tile.description}</BodyText>
                  <TextLink size="medium" weight="bold" href={tile.ctaUrl}>
                    {tile.ctaLabel}
                  </TextLink>
                </BoostTileContent>
              </BoostTile>
            ))}
          </BoostTiles>
        </LayoutContainer>
      </WaysToBoostSection>}
    </Page>
  </LoyaltySpendPointsPricingContext.Provider>
}

export default LoyaltySpendPointsPage
